import type { System, WireSpec } from "../types";
import { CONTINUOUS_LOAD_HEADROOM, FUSE_RATINGS, MIN_GENERAL_CIRCUIT_MM2, STARTING_VDROP_TARGET_PCT, SURGE_HOLD_FACTOR, SELECTABLE_WIRE_TABLE, WIRE_TABLE } from "../wire-tables";
import type { DesignProfile, ElectricalDesignPolicy } from "./contracts";
export type { DesignProfile, DesignProfileId, ElectricalDesignPolicy, FuseSelectionPolicy } from "./contracts";

/** Deeply immutable registry: a profile ID identifies one reviewed calculation revision. */
function freeze<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
}

const MARINE_POLICY: ElectricalDesignPolicy = {
  wireTable: WIRE_TABLE.map(wire => ({ ...wire })),
  manualWireTable: SELECTABLE_WIRE_TABLE.map(wire => ({ ...wire })),
  minimumWireMm2: MIN_GENERAL_CIRCUIT_MM2,
  continuousLoadHeadroom: CONTINUOUS_LOAD_HEADROOM,
  surgeHoldFactor: SURGE_HOLD_FACTOR,
  fuseRatings: [...FUSE_RATINGS], crankingMinimumFuseA: 250,
  engineBayDerating: 0.82,
  voltageDrop: { criticalPct: 3, nonCriticalPct: 10, startingPct: STARTING_VDROP_TARGET_PCT,
    classifyCatalogLoads: true, mainFeederPct: 3 },
  protection: { batteryDistanceM: 0.175, batteryTerminalFuseAdvisory: true },
  coverage: { ac: true, cranking: true, pv: true, engineBay: true, chassisReturn: true, signals: true,
    minDcVoltage: 0, maxDcVoltage: Infinity, minCurrentA: 0, enforceTableCeiling: false },
};

/** RVIA LV 2020 §3-2 / Table 1, unrestricted bundling, stranded copper (§4-1).
 * §3-2 identifies these as conductor ampacities and limits protection to them.
 * §4-2 requires ≥90°C insulation; the hotter engine/exhaust case is not covered.
 * Native AWG areas use the same NBS conversion as the existing calculator.
 * These are Table 1 limits, not an assertion that every installed cable qualifies. */
export const RV_LV2020_TABLE1: readonly WireSpec[] = freeze([
  [20, 3], [18, 6], [16, 8], [14, 15], [12, 20], [10, 30], [8, 40], [6, 55], [4, 75], [2, 100],
].map(([gauge, ampacity]) => ({
  mm2: Number((Math.PI / 4 * (0.127 * 92 ** ((36 - gauge) / 39)) ** 2).toFixed(5)),
  awg: String(gauge), ampacity,
})));

export const DESIGN_PROFILES: readonly DesignProfile[] = freeze([
  { id: "marine-abyc-v1", label: "Marine · ABYC", labelKey: "design_profile.marine", scopeKey: "design_profile.marine_scope",
    status: "available", scope: "Existing ABYC-based planning calculations for 105°C marine copper; existing AC limitations remain.",
    citations: [{ title: "Existing ABYC E-11 calculation basis", url: "https://crimp.studio/methodology/", section: "Ampacity, voltage drop and protection" }],
    policy: MARINE_POLICY },
  { id: "rv-us-lv2020-v1", label: "RV / camper · DC planning", labelKey: "design_profile.rv_us_lv2020", scopeKey: "design_profile.rv_scope", detailsKey: "design_profile.rv_basis",
    status: "available", scope: "DC planning based on ANSI/RVIA LV (2020), Table 1: stranded copper, at least 90°C insulation, ordinary routing outside engine compartments and more than 10 inches (25.4 cm) from exhaust, connected load at least 1 A, rated at most 60 V DC. Engine/exhaust routes, starting, PV and chassis-return circuits require separate verification. AC installation and national-code compliance are not validated, including current US or Canadian requirements.",
    citations: [{ title: "RVIA Low Voltage Systems in Conversion and Recreational Vehicles (2020)", url: "https://manuals.plus/m/6bbdbd94f702a708ba3152af97b894df3c9e5bcf356bde028519a5a323c49322.pdf", section: "§§1-2, 3-2, 4-1, 4-2; Table 1" }],
    policy: { ...MARINE_POLICY, wireTable: RV_LV2020_TABLE1, manualWireTable: RV_LV2020_TABLE1, minimumWireMm2: RV_LV2020_TABLE1[0].mm2,
      // These remain conservative planner choices, not claimed RVIA mandates.
      continuousLoadHeadroom: 1.25, engineBayDerating: 1, crankingMinimumFuseA: undefined,
      voltageDrop: { criticalPct: 3, nonCriticalPct: 3, startingPct: 2, classifyCatalogLoads: false },
      // LV2020 §3-5 uses the source-to-vehicle connection; no such location is
      // represented in the drawing. Do not relabel the marine battery-post walk.
      protection: { batteryTerminalFuseAdvisory: false },
      coverage: { ac: false, cranking: false, pv: false, engineBay: false, chassisReturn: false, signals: false,
        minDcVoltage: 0, maxDcVoltage: 60, minCurrentA: 1, enforceTableCeiling: true },
    } },
]);

export function resolveDesignProfile(system: Pick<System, "designProfileId">): DesignProfile {
  const id = system.designProfileId ?? "marine-abyc-v1";
  return DESIGN_PROFILES.find(profile => profile.id === id) ?? freeze({
    id, label: "Unknown profile", labelKey: "design_profile.unknown", scopeKey: "design_profile.unknown_scope", status: "unavailable",
    scope: "This saved profile is unavailable. Existing values need verification; automatic sizing is not established.",
    citations: [], policy: MARINE_POLICY,
  });
}

export function designProfileStatus(system: Pick<System, "designProfileId">) {
  const profile = resolveDesignProfile(system);
  return { profileId: profile.id, status: profile.status,
    code: profile.status === "unavailable" ? "unknown-profile" as const
      : profile.id === "marine-abyc-v1" ? "legacy-marine" as const : "bounded-rv" as const,
    label: profile.label, citations: profile.citations, scope: profile.scope };
}
